import { Button, Card, Form, Input, Select, Space, Table, Tag } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Row = {
  id: string;
  send_no: string;
  template_code?: string;
  template_name?: string;
  status: string;
  recipient_count: number;
  sent_count: number;
  failed_count: number;
};

export default function CommsSendListPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.comms.manage') || user?.permissions?.includes('education.admin')
  );
  const [status, setStatus] = useState<string | undefined>();
  const [q, setQ] = useState('');

  const list = useQuery({
    queryKey: ['education-comm-sends', status, q],
    queryFn: async () => {
      const res = await api.get<Row[]>('/education/comms/sends', {
        params: { status, q: q || undefined },
      });
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  return (
    <EducationPageChrome
      title={t('commSends')}
      description={t('commSendsIntro')}
      extra={
        <Space>
          <Button onClick={() => navigate('/education/comms')}>{t('comms')}</Button>
          {canManage ? (
            <Button type="primary" onClick={() => navigate('/education/comms/sends/new')}>
              {t('newCommSend')}
            </Button>
          ) : null}
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small">
          <Form layout="inline" onFinish={() => list.refetch()}>
            <Form.Item label={t('status')}>
              <Select
                allowClear
                style={{ minWidth: 140 }}
                value={status}
                onChange={(v) => setStatus(v || undefined)}
                options={[
                  { value: 'draft', label: t('statusDraft') },
                  { value: 'sent', label: t('statusSent') },
                  { value: 'failed', label: t('statusFailed') },
                  { value: 'cancelled', label: t('statusCancelled') },
                ]}
              />
            </Form.Item>
            <Form.Item label={t('search')}>
              <Input value={q} onChange={(e) => setQ(e.target.value)} allowClear style={{ width: 160 }} />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={list.isFetching}>
              {t('filter')}
            </Button>
          </Form>
        </Card>
        <Card size="small">
          <Table
            rowKey="id"
            size="small"
            loading={list.isLoading}
            dataSource={list.data ?? []}
            pagination={{ pageSize: 25 }}
            onRow={(row) => ({
              onClick: () => navigate(`/education/comms/sends/${row.id}`),
              style: { cursor: 'pointer' },
            })}
            columns={[
              { title: t('sendNo'), dataIndex: 'send_no', width: 160 },
              {
                title: t('templateCode'),
                render: (_: unknown, row: Row) => row.template_code || row.template_name || '—',
              },
              { title: t('recipients'), dataIndex: 'recipient_count', width: 100 },
              { title: t('sent'), dataIndex: 'sent_count', width: 80 },
              { title: t('failed'), dataIndex: 'failed_count', width: 80 },
              {
                title: t('status'),
                dataIndex: 'status',
                width: 100,
                render: (v: string) => <Tag>{v}</Tag>,
              },
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
